// Postmenopause headline card — renders the live Vitality Score (0–100)
// computed from sleep duration, HRV, and resting HR vs the user's own
// baseline. Replaces MenopauseScoreCard when stage === "Postmenopause".
//
// Math lives in src/knowmeno/vitalityScore.ts — this component is
// presentation only. Peri/meno is untouched.

import React, { useEffect, useState } from "react";
import { getScoreLabel } from "./scoreLabel";
import {
  loadVitalityScoreForCurrentUser,
  type VitalityScore,
} from "./vitalityScore";

const WH = "#ffffff";
const TD = "#4B1F4F";
const GR = "#6E4B71";
const GD = "#6E4B71";
const ACCENT = "#FF5C7A";

// Band colors come from the shared menoScore.BANDS table via
// vitalityScore.vitalityBandFor — do NOT redeclare them here.

export function VitalityScoreCard({
  stage,
  onOpenDetail,
}: {
  stage?: string | null;
  onOpenDetail?: () => void;
}) {
  const label = getScoreLabel(stage).toUpperCase();
  const [data, setData] = useState<VitalityScore | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadVitalityScoreForCurrentUser()
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isOk = data?.status === "ok" && data.score !== null && data.band;
  const top2 = data?.contributions.slice(0, 2) ?? [];

  return (
    <div
      onClick={onOpenDetail}
      style={{
        background: WH,
        border: "0.5px solid #E8D5DA",
        borderRadius: 18,
        padding: "18px 18px 16px",
        marginBottom: 14,
        cursor: onOpenDetail ? "pointer" : "default",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: ACCENT, fontWeight: 600, letterSpacing: "0.08em" }}>
          {label} · TODAY
        </div>
        {isOk && data?.band && (
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: TD,
              background: data.band.color,
              padding: "3px 10px",
              borderRadius: 999,
            }}
          >
            {data.band.label}
          </div>
        )}
      </div>

      {!loaded && (
        <div style={{ fontSize: 13, color: GR, padding: "6px 0" }}>Loading…</div>
      )}

      {loaded && isOk && data && (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
            <div style={{ fontSize: 44, fontWeight: 700, color: TD, lineHeight: 1 }}>{data.score}</div>
            <div style={{ fontSize: 12, color: GR }}>/ 100</div>
          </div>
          <div style={{ fontSize: 13.5, color: GD, marginBottom: 10, lineHeight: 1.4 }}>
            {data.lead}
          </div>
          {top2.length > 0 && (
            <div style={{ borderTop: "1px solid #F3E6EA", paddingTop: 10 }}>
              <div style={{ fontSize: 10.5, color: GR, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 6 }}>
                WHAT'S DRIVING IT
              </div>
              {top2.map((c) => (
                <div
                  key={c.key}
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0" }}
                >
                  <span style={{ fontSize: 12.5, color: GD }}>{c.label}</span>
                  <span style={{ fontSize: 12, color: GR }}>{c.detail}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {loaded && !isOk && (
        <>
          <div style={{ fontSize: 15, color: GD, fontWeight: 500, marginBottom: 6, lineHeight: 1.4 }}>
            {data?.lead ?? "Your recovery-state score is coming soon."}
          </div>
          <div style={{ fontSize: 12, color: GR, lineHeight: 1.5 }}>
            A daily 0–100 number based on your sleep, HRV, and resting heart rate vs. your own baseline.
            Higher = better recovered.
          </div>
        </>
      )}

      {onOpenDetail && (
        <div style={{ marginTop: 10, textAlign: "right" }}>
          <span style={{ fontSize: 11.5, color: TD, fontWeight: 500 }}>View detail →</span>
        </div>
      )}
    </div>
  );
}
